import { useTheme } from '@/shared/context'
import { UiFloatButton } from '@/shared/ui'
import { useLayoutSettings } from '@/shared/ui'
import {
  type ContentWidth,
  type LayoutMode,
  MAX_CUSTOM_COLORS,
  type PageBg,
  SPIN_ICONS,
  SPIN_ICON_LABEL,
  getRadiusPx,
} from '@/shared/ui'
import { THEME_COLOR_LABEL, type ThemeColor, themeTokens } from '@/shared/ui'
import { THEME_PRESETS } from '@/shared/ui'
import { bodyTextContrast, isHexColor } from '@/shared/ui'
import { CheckOutlined } from '@/shared/ui/icons'
import { PlusOutlined as Plus, SettingOutlined as Settings } from '@/shared/ui/icons'
import UiAlert from '@/shared/ui/ui-alert'
import UiButton from '@/shared/ui/ui-button'
import UiColorPicker from '@/shared/ui/ui-color-picker'
import UiDrawer from '@/shared/ui/ui-drawer'
import { notify } from '@/shared/ui/ui-notification'
import UiSelect from '@/shared/ui/ui-select'
import UiSlider from '@/shared/ui/ui-slider'
import { SpinIconPreview } from '@/shared/ui/ui-spin/spin-icons'
import UiSwitch from '@/shared/ui/ui-switch'
import UiTooltip from '@/shared/ui/ui-tooltip'
import { useRef, useState } from 'react'
import './LayoutSettingDrawer.scss'

/**
 * O mau lay THANG tu themeTokens: mau hien thi = mau that cua theme do.
 * ds-guard-ignore: vi phạm có sẵn trước khi chuyển từ components (C3)
 * Danh sach hardcode truoc day lech thuc te (vd o "Tech Blue #1890ff" tro toi theme
 * ds-guard-ignore: vi phạm có sẵn trước khi chuyển từ components (C3)
 * `primary` von la #267DC4), nen chon mot dang xem mot neo.
 */
const THEME_COLORS = (Object.keys(THEME_COLOR_LABEL) as ThemeColor[]).map((key) => ({
  key,
  color: themeTokens(key).colorPrimary,
  label: THEME_COLOR_LABEL[key],
}))

const PAGE_BGS: { key: Exclude<PageBg, 'custom'>; label: string }[] = [
  { key: 'page', label: 'Mặc định' },
  { key: 'surface', label: 'Trắng (nền thẻ)' },
  { key: 'sunken', label: 'Xám nhạt' },
]

const RADIUS_SLIDER_MARKS: Record<number, string> = {
  0: '0',
  4: 'Vuông',
  8: 'Vừa',
  12: 'Tròn',
  16: '16',
}

interface LayoutSettingDrawerProps {
  open?: boolean
  onClose?: () => void
}

export default function LayoutSettingDrawer({
  open: controlledOpen,
  onClose: controlledOnClose,
}: LayoutSettingDrawerProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const handleClose = () => {
    if (isControlled) {
      controlledOnClose?.()
    } else {
      setInternalOpen(false)
    }
  }
  const { settings, setSetting, reset } = useLayoutSettings()
  const { theme, themeMode, setTheme, setThemeMode, themeColor, setThemeColor } = useTheme()
  const pendingColor = useRef('')
  const hasCustomPrimary = isHexColor(settings.customPrimary)
  const customBgOn = settings.pageBg === 'custom' && isHexColor(settings.customPageBg)
  // Chữ thường của theme hiện tại trên nền tuỳ chọn: dưới 4.5:1 thì cảnh báo khó đọc.
  const customBgLowContrast = customBgOn && bodyTextContrast(theme, settings.customPageBg) < 4.5
  const applyCustomPrimary = (color: string) => {
    setSetting('customPrimary', color)
    setSetting('activePreset', undefined)
  }
  const addCustomColor = (color: string) => {
    const hex = color.toLowerCase()
    if (!isHexColor(hex)) return
    if (!settings.customColors.includes(hex)) {
      setSetting('customColors', [...settings.customColors, hex].slice(0, MAX_CUSTOM_COLORS))
    }
    applyCustomPrimary(hex)
  }
  const isActiveCustom = (color: string) =>
    hasCustomPrimary && settings.customPrimary.toLowerCase() === color
  const removeCustomColor = (color: string) => {
    setSetting(
      'customColors',
      settings.customColors.filter((c) => c !== color)
    )
  }
  const siderAvailable = settings.showMenu && settings.layout !== 'top'

  const copySettings = async () => {
    const { colorWeak, ...rest } = settings
    await navigator.clipboard.writeText(JSON.stringify(rest, null, 2))
    notify.success('Đã sao chép cấu hình bố cục (JSON) vào bộ nhớ tạm')
  }

  // Về mặc định gốc: bố cục + sáng/tối (mặc định: theo hệ thống) + màu chủ đạo (mặc định của UIProvider).
  const resetAll = () => {
    reset()
    setThemeMode('system')
    setThemeColor('primary')
    notify.success('Đã đặt lại bố cục, giao diện theo hệ thống và màu chủ đạo về mặc định')
  }

  return (
    <>
      {!isControlled && (
        <UiFloatButton
          icon={<Settings size={18} />}
          tooltip="Thiết lập bố cục"
          onClick={() => setInternalOpen(true)}
          style={{ right: 16, bottom: 72 }}
        />
      )}
      <UiDrawer
        title="Thiết lập bố cục"
        placement="right"
        size="sm"
        mask={{ closable: true }}
        styles={{ mask: { background: 'transparent' } }}
        open={open}
        onClose={handleClose}
      >
        <div className="setting-drawer-container">
          {/* ==================================================== */}
          {/* 0. Theme nhanh (Preset)                              */}
          {/* ==================================================== */}
          <div className="setting-drawer-title">Theme nhanh</div>
          <div className="theme-preset-grid">
            {THEME_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                aria-label={preset.label}
                className={`theme-preset-card${settings.activePreset === preset.key ? ' active' : ''}`}
                onClick={() => {
                  setSetting('customPrimary', preset.colorPrimary)
                  setSetting('radiusLevel', preset.radiusLevel)
                  setSetting('activePreset', preset.key)
                  setTheme(preset.mode ?? 'light')
                }}
              >
                <span className="preset-emoji">{preset.emoji}</span>
                <span className="preset-label">{preset.label}</span>
                <span className="preset-color-dots">
                  <span className="preset-dot" style={{ backgroundColor: preset.colorPrimary }} />
                  <span
                    className="preset-dot preset-dot-bg"
                    style={{ backgroundColor: preset.surfaces.page }}
                  />
                  {preset.colorSecondary && (
                    <span
                      className="preset-dot"
                      style={{ backgroundColor: preset.colorSecondary }}
                    />
                  )}
                </span>
              </button>
            ))}
          </div>

          <div className="setting-drawer-divider" />

          {/* ==================================================== */}
          {/* 1. Kiểu giao diện                                */}
          {/* ==================================================== */}
          <div className="setting-drawer-title">Kiểu giao diện</div>
          <div className="setting-drawer-block-cards">
            {/* Light Style */}
            <UiTooltip title="Sáng" placement="top">
              <div
                className={`setting-card-item ${themeMode === 'light' ? 'active' : ''}`}
                onClick={() => setTheme('light')}
              >
                <div className="style-card-light">
                  <div className="card-sider" />
                  <div className="card-body">
                    <div className="card-header" />
                    <div className="card-content" />
                  </div>
                </div>
                {themeMode === 'light' && <CheckOutlined className="card-check-icon" />}
              </div>
            </UiTooltip>

            {/* Dark Style */}
            <UiTooltip title="Tối" placement="top">
              <div
                className={`setting-card-item ${themeMode === 'dark' ? 'active' : ''}`}
                onClick={() => setTheme('dark')}
              >
                <div className="style-card-dark">
                  <div className="card-sider" />
                  <div className="card-body">
                    <div className="card-header" />
                    <div className="card-content" />
                  </div>
                </div>
                {themeMode === 'dark' && <CheckOutlined className="card-check-icon" />}
              </div>
            </UiTooltip>

            {/* System Style */}
            <UiTooltip title="Theo hệ thống" placement="top">
              <div
                className={`setting-card-item ${themeMode === 'system' ? 'active' : ''}`}
                onClick={() => setThemeMode('system')}
              >
                <div className="style-card-system">
                  <div className="style-card-light">
                    <div className="card-sider" />
                    <div className="card-body">
                      <div className="card-header" />
                      <div className="card-content" />
                    </div>
                  </div>
                  <div className="style-card-dark">
                    <div className="card-sider" />
                    <div className="card-body">
                      <div className="card-header" />
                      <div className="card-content" />
                    </div>
                  </div>
                </div>
                {themeMode === 'system' && <CheckOutlined className="card-check-icon" />}
              </div>
            </UiTooltip>
          </div>

          {/* ==================================================== */}
          {/* 2. Màu chủ đạo                                       */}
          {/* ==================================================== */}
          <div className="setting-drawer-title">Màu chủ đạo</div>
          <div className="theme-color-list">
            {THEME_COLORS.map((item) => (
              <UiTooltip key={item.key} title={item.label} placement="top">
                <div
                  className="theme-color-block"
                  style={{ backgroundColor: item.color }}
                  onClick={() => {
                    setThemeColor(item.key)
                    setSetting('customPrimary', '')
                    setSetting('activePreset', undefined)
                  }}
                >
                  {themeColor === item.key && !hasCustomPrimary && (
                    <CheckOutlined className="color-check-icon" />
                  )}
                </div>
              </UiTooltip>
            ))}
            <div className="page-bg-list page-bg-list-wrap">
              {settings.customColors.map((color) => (
                <span key={color} className="custom-color-item">
                  <UiTooltip title={color} placement="top">
                    <button
                      type="button"
                      aria-label={`Dùng màu ${color}`}
                      className={`page-bg-block ${isActiveCustom(color) ? 'active' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => applyCustomPrimary(color)}
                    />
                  </UiTooltip>
                  <button
                    type="button"
                    className="custom-color-remove"
                    aria-label={`Xoá màu ${color}`}
                    onClick={() => removeCustomColor(color)}
                  >
                    ×
                  </button>
                </span>
              ))}
              {settings.customColors.length < MAX_CUSTOM_COLORS && (
                <UiTooltip title="Thêm màu" placement="top">
                  <span>
                    <UiColorPicker
                      disabledAlpha
                      onChange={(c) => {
                        pendingColor.current = c.toHexString()
                      }}
                      onOpenChange={(isOpen) => {
                        if (isOpen) {
                          pendingColor.current = ''
                        } else if (pendingColor.current) {
                          addCustomColor(pendingColor.current)
                          pendingColor.current = ''
                        }
                      }}
                    >
                      <div
                        className="page-bg-block custom-color-block"
                        aria-label="Thêm màu chủ đạo"
                      >
                        <Plus size={12} />
                      </div>
                    </UiColorPicker>
                  </span>
                </UiTooltip>
              )}
            </div>
          </div>

          <div className="setting-drawer-divider" />

          {/* ==================================================== */}
          {/* 3. Kiểu điều hướng                                   */}
          {/* ==================================================== */}
          <div className="setting-drawer-title">Kiểu điều hướng</div>
          <div className="setting-drawer-block-cards">
            {/* Side Menu */}
            <UiTooltip title="Menu bên trái" placement="top">
              <div
                className={`setting-card-item ${settings.layout === 'side' ? 'active' : ''}`}
                onClick={() => setSetting('layout', 'side' as LayoutMode)}
              >
                <div className="nav-card-side">
                  <div className="card-sider" />
                  <div className="card-body">
                    <div className="card-header" />
                    <div className="card-content" />
                  </div>
                </div>
                {settings.layout === 'side' && <CheckOutlined className="card-check-icon" />}
              </div>
            </UiTooltip>

            {/* Top Menu */}
            <UiTooltip title="Menu trên đầu" placement="top">
              <div
                className={`setting-card-item ${settings.layout === 'top' ? 'active' : ''}`}
                onClick={() => setSetting('layout', 'top' as LayoutMode)}
              >
                <div className="nav-card-top">
                  <div className="card-header" />
                  <div className="card-content" />
                </div>
                {settings.layout === 'top' && <CheckOutlined className="card-check-icon" />}
              </div>
            </UiTooltip>

            {/* Mix Menu */}
            <UiTooltip title="Menu hỗn hợp" placement="top">
              <div
                className={`setting-card-item ${settings.layout === 'mix' ? 'active' : ''}`}
                onClick={() => {
                  setSetting('layout', 'mix' as LayoutMode)
                  setSetting('splitMenus', false)
                }}
              >
                <div className="nav-card-mix">
                  <div className="card-header" />
                  <div className="card-bottom">
                    <div className="card-sider" />
                    <div className="card-content" />
                  </div>
                </div>
                {settings.layout === 'mix' && <CheckOutlined className="card-check-icon" />}
              </div>
            </UiTooltip>
          </div>

          {/* Kiểu điều hướng Options */}
          <div className="setting-row">
            <span>Độ rộng nội dung</span>
            <UiSelect
              value={settings.contentWidth}
              onChange={(value) => setSetting('contentWidth', value as ContentWidth)}
              size="small"
              style={{ width: 96 }}
              options={[
                { label: 'Cố định', value: 'Fixed' },
                { label: 'Tràn', value: 'Fluid' },
              ]}
            />
          </div>

          <div className="setting-row">
            <span>Cố định thanh đầu trang</span>
            <UiSwitch
              size="small"
              checked={settings.fixedHeader}
              onChange={(checked) => setSetting('fixedHeader', checked)}
            />
          </div>

          <div className="setting-row">
            <span className={settings.layout === 'top' ? 'disabled-label' : ''}>
              Cố định menu bên trái
            </span>
            <UiSwitch
              size="small"
              checked={settings.fixSiderbar}
              disabled={settings.layout === 'top'}
              onChange={(checked) => setSetting('fixSiderbar', checked)}
            />
          </div>

          <div className="setting-row">
            <span className={settings.layout !== 'mix' ? 'disabled-label' : ''}>
              Tách menu cấp 1 lên đầu trang
            </span>
            <UiSwitch
              size="small"
              checked={settings.splitMenus}
              disabled={settings.layout !== 'mix'}
              onChange={(checked) => setSetting('splitMenus', checked)}
            />
          </div>

          <div className="setting-row">
            <span className={settings.layout === 'top' ? 'disabled-label' : ''}>
              Hiện tên nhóm menu
            </span>
            <UiSwitch
              size="small"
              checked={settings.siderMenuType === 'group'}
              disabled={settings.layout === 'top'}
              onChange={(checked) => setSetting('siderMenuType', checked ? 'group' : 'sub')}
            />
          </div>

          <div className="setting-drawer-divider" />

          {/* ==================================================== */}
          {/* 4. Hiển thị các vùng                                 */}
          {/* ==================================================== */}
          <div className="setting-drawer-title">Hiển thị các vùng</div>

          <div className="setting-row">
            <span>Thanh đầu trang</span>
            <UiSwitch
              size="small"
              checked={settings.showHeader}
              onChange={(checked) => setSetting('showHeader', checked)}
            />
          </div>

          <div className="setting-row">
            <span>Chân trang</span>
            <UiSwitch
              size="small"
              checked={settings.showFooter}
              onChange={(checked) => setSetting('showFooter', checked)}
            />
          </div>

          <div className="setting-row">
            <span className={settings.layout === 'top' ? 'disabled-label' : ''}>Menu bên trái</span>
            <UiSwitch
              size="small"
              checked={settings.showMenu}
              disabled={settings.layout === 'top'}
              onChange={(checked) => setSetting('showMenu', checked)}
            />
          </div>

          <div className="setting-drawer-divider" />

          <div className="setting-drawer-title">Giao diện chung</div>

          <div className="setting-row">
            <span>Màu nền trang</span>
            <div className="page-bg-list">
              {PAGE_BGS.map((item) => (
                <UiTooltip key={item.key} title={item.label} placement="top">
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={item.label}
                    className={`page-bg-block ${settings.pageBg === item.key ? 'active' : ''}`}
                    style={{ backgroundColor: `var(--vbdt-color-bg-${item.key})` }}
                    onClick={() => setSetting('pageBg', item.key)}
                    onKeyDown={(e) => e.key === 'Enter' && setSetting('pageBg', item.key)}
                  />
                </UiTooltip>
              ))}
              <UiTooltip title="Tuỳ chỉnh" placement="top">
                <span>
                  <UiColorPicker
                    value={customBgOn ? settings.customPageBg : undefined}
                    disabledAlpha
                    onChangeComplete={(c) => {
                      setSetting('customPageBg', c.toHexString())
                      setSetting('pageBg', 'custom')
                    }}
                  >
                    <div
                      className={`page-bg-block custom-color-block ${customBgOn ? 'active' : ''}`}
                      style={customBgOn ? { backgroundColor: settings.customPageBg } : undefined}
                      aria-label="Tuỳ chỉnh màu nền trang"
                    >
                      {!customBgOn && <Plus size={12} />}
                    </div>
                  </UiColorPicker>
                </span>
              </UiTooltip>
            </div>
          </div>
          {customBgLowContrast && (
            <UiAlert
              type="warning"
              showIcon
              // ds-guard-ignore: vi phạm có sẵn trước khi chuyển từ components (C3)
              style={{ marginBottom: 8 }}
              message="Màu nền này làm chữ khó đọc ở giao diện hiện tại. Hãy chọn màu sáng hơn (giao diện sáng) hoặc tối hơn (giao diện tối)."
            />
          )}

          <div
            className="setting-row"
            style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <span>Bo góc</span>
              <span style={{ color: 'var(--vbdt-color-text-secondary)', fontSize: 12 }}>
                {(() => {
                  const px = getRadiusPx(settings.radiusLevel)
                  if (px === 0) return 'Vuông (0px)'
                  if (px === 4) return 'Vuông nhẹ (4px)'
                  if (px === 8) return 'Vừa (8px)'
                  if (px === 12) return 'Tròn (12px)'
                  if (px === 16) return 'Rất tròn (16px)'
                  return `${px}px`
                })()}
              </span>
            </div>
            <div style={{ width: '100%', padding: '0 8px 6px' }}>
              <UiSlider
                min={0}
                max={16}
                step={1}
                value={getRadiusPx(settings.radiusLevel)}
                onChange={(val) => {
                  setSetting('radiusLevel', val)
                }}
                marks={RADIUS_SLIDER_MARKS}
                tooltip={{ formatter: (val) => `${val}px` }}
              />
            </div>
          </div>

          <div className="setting-row">
            <span>Giao diện gọn</span>
            <UiSwitch
              size="small"
              checked={settings.compact}
              onChange={(checked) => setSetting('compact', checked)}
            />
          </div>

          <div className="setting-row">
            <span className={!siderAvailable ? 'disabled-label' : ''}>Độ rộng menu bên trái</span>
            <UiSelect
              value={settings.siderWidth}
              onChange={(value) => setSetting('siderWidth', value as number)}
              size="small"
              style={{ width: 96 }}
              disabled={!siderAvailable}
              options={[
                { label: 'Hẹp', value: 220 },
                { label: 'Vừa', value: 250 },
                { label: 'Rộng', value: 290 },
              ]}
            />
          </div>

          <div className="setting-row">
            <span>Hiện đường dẫn trang</span>
            <UiSwitch
              size="small"
              checked={settings.showBreadcrumb}
              onChange={(checked) => setSetting('showBreadcrumb', checked)}
            />
          </div>

          <div className="setting-row">
            <span>Hiện logo hệ thống</span>
            <UiSwitch
              size="small"
              checked={settings.showMenuHeader}
              onChange={(checked) => setSetting('showMenuHeader', checked)}
            />
          </div>

          <div className="setting-row setting-row--stack">
            <span>Icon loading</span>
            <div className="spin-icon-grid">
              {SPIN_ICONS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  aria-pressed={settings.spinIcon === kind}
                  className={`spin-icon-card${settings.spinIcon === kind ? ' active' : ''}`}
                  onClick={() => setSetting('spinIcon', kind)}
                >
                  <span className="spin-icon-preview">
                    <SpinIconPreview kind={kind} />
                  </span>
                  <span className="spin-icon-label">{SPIN_ICON_LABEL[kind]}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="setting-row">
            <span>Chế độ màu yếu</span>
            <UiSwitch
              size="small"
              checked={settings.colorWeak}
              onChange={(checked) => setSetting('colorWeak', checked)}
            />
          </div>

          <div className="setting-drawer-divider" />

          {/* ==================================================== */}
          {/* 5. Actions & Dev Alert                               */}
          {/* ==================================================== */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <UiButton type="primary" size="small" onClick={copySettings} style={{ flex: 1 }}>
              Sao chép JSON
            </UiButton>
            <UiButton size="small" onClick={resetAll}>
              Đặt lại tất cả
            </UiButton>
          </div>

          <UiAlert
            type="info"
            showIcon
            message="Bố cục được lưu trên trình duyệt này; màu chủ đạo sẽ về mặc định khi tải lại trang. Nút Đặt lại tất cả đưa cả ba (bố cục, sáng/tối, màu chủ đạo) về mặc định."
          />
        </div>
      </UiDrawer>
    </>
  )
}
